<script lang="ts">
    import type { Snippet } from "svelte"
    import { mdiMinus, mdiPlus } from "@mdi/js"
    import { buildTableRowActionSet, type TableSelectionContext } from "@kontextwerk/web-sdk/core/layout/table"
    import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
    import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"

    import Table, {
        type ColumnDef,
        type DataRow,
        type TableGetRows,
        type TableRowsUpdater,
    } from "@kontextwerk/web-sdk/ui/layout/Table.svelte"
    import {
        STABLE_ROW_NUMBER_COLUMN_ID,
        type StableTableSortState,
    } from "../../../../../../../../../../../../_shared/_logic/stable-row-numbers"
    import type { LoadListTableRow } from "../../_shared/_logic/load-list-row-model"
    import AddressCell from "./_modules/address-cell"
    import UnitCell from "./_modules/unit-cell"
    import WarningCell from "./_modules/warning-cell"
    import AddressRowActions from "./_actions/AddressRowActions.actions"
    import type { AddressTableFilters } from "../../_shared/_logic/table-model"

    type Props = {
        orderCell: Snippet<[LoadListTableRow, string]>
        columns: ColumnDef[]
        getRows: TableGetRows
        defaultSort?: StableTableSortState
        pageSize: number
        refreshKey: string
        filters: AddressTableFilters
        onFiltersChange: (filters: AddressTableFilters) => void
        locked?: boolean
        readOnly?: boolean
        organizationId: string
        seedProperties: Property[]
        propertyForId: (propertyId: string) => Property | null
        onSetRefreshFunction: (fn: () => void) => void
        onSetRowsUpdater: (updater: TableRowsUpdater) => void
        onUnitSelectionChange: (row: LoadListTableRow, unitId: string | null) => void
        onAddressChange: (row: LoadListTableRow, property: Property | null) => void
        onEditUnits: (row: LoadListTableRow) => void
        onRemoveRow: (row: LoadListTableRow) => void
        onRestoreRow: (row: LoadListTableRow) => void
        onRemoveRows: (rows: LoadListTableRow[]) => void
        onRestoreRows: (rows: LoadListTableRow[]) => void
        onCopyRow: (row: LoadListTableRow) => void
        onShowDetails: (row: LoadListTableRow) => void
        onRowsLoaded?: (rows: LoadListTableRow[]) => void
        onSelectionChange?: (context: TableSelectionContext) => void
    }

    const {
        orderCell,
        columns,
        getRows,
        defaultSort = null,
        pageSize,
        refreshKey,
        filters,
        onFiltersChange,
        locked = false,
        readOnly = false,
        organizationId,
        seedProperties,
        propertyForId,
        onSetRefreshFunction,
        onSetRowsUpdater,
        onUnitSelectionChange,
        onAddressChange,
        onEditUnits,
        onRemoveRow,
        onRestoreRow,
        onRemoveRows,
        onRestoreRows,
        onCopyRow,
        onShowDetails,
        onRowsLoaded,
        onSelectionChange,
    }: Props = $props()

    type RowControlCommand = {
        kind: "remove" | "restore"
        disabled?: boolean
        onClick?: () => void | Promise<void>
    }

    const rowControlsFor = (row: DataRow): RowControlCommand[] => {
        const addressRow = row as LoadListTableRow
        const controlsDisabled = locked || readOnly

        if (addressRow.removed) {
            return [
                {
                    kind: "restore",
                    disabled: controlsDisabled,
                    onClick: () => onRestoreRow(addressRow),
                },
            ]
        }

        return [
            {
                kind: "remove",
                disabled: controlsDisabled,
                onClick: () => onRemoveRow(addressRow),
            },
        ]
    }

    const rowStateFor = (row: DataRow) => {
        const addressRow = row as LoadListTableRow

        if (!addressRow.removed) return { selectable: !locked && !readOnly }

        return {
            className: "batch-row-removed",
            mutedReason: "Diese Zeile wird in diesem Schritt ausgelassen.",
            selectable: !locked && !readOnly,
        }
    }

    const applyBulkAction = (context: TableSelectionContext, removed: boolean) => {
        const selectedRows = (context.selectedRows as LoadListTableRow[]).filter(
            (row) => Boolean(row.removed) !== removed
        )

        if (locked || readOnly || !selectedRows.length) return

        if (removed) onRemoveRows(selectedRows)
        else onRestoreRows(selectedRows)

        context.clearSelection()
    }
</script>

{#snippet renderCell(column: ColumnDef, row: DataRow)}
    {@const currentRow = row as LoadListTableRow}
    {#if column.id === STABLE_ROW_NUMBER_COLUMN_ID}
        {currentRow[STABLE_ROW_NUMBER_COLUMN_ID]}
    {:else if column.id === "unit"}
        <UnitCell
            row={currentRow}
            disabled={locked || readOnly || currentRow.removed}
            onSelectionChange={onUnitSelectionChange}
        />
    {:else if column.id === "warning"}
        <WarningCell row={currentRow} />
    {:else if ["order", "price", "duration", "employees", "due"].includes(column.id)}
        {@render orderCell(currentRow, column.id)}
    {:else}
        <AddressCell
            row={currentRow}
            {organizationId}
            {seedProperties}
            selectedProperty={currentRow.propertyId ? propertyForId(currentRow.propertyId) : null}
            disabled={locked || readOnly || currentRow.removed}
            onChange={onAddressChange}
        />
    {/if}
{/snippet}

<div class="batch-address-table">
    <div class="batch-address-filters form light" aria-label="Liegenschaften filtern">
        <div class="row">
            <Input
                type="text"
                placeholder="Suche"
                label="Suche"
                tooltip="Sucht in Adressen und Ansprechpartnern. Mehrere Suchbegriffe werden gemeinsam berücksichtigt."
                bind:value={
                    () => filters.search, (value) => onFiltersChange({ ...filters, search: String(value ?? "") })
                }
            />
            <Input
                type="select"
                label="Status"
                options={[
                    { name: "Alle", value: "all" },
                    { name: "Prüfbedarf", value: "review" },
                    { name: "Fehlkonfiguration", value: "misconfigured" },
                ]}
                clearable={false}
                tooltip="Prüfbedarf: alle Fehlkonfigurationen sowie mögliche Doppelaufträge oder unklare Arbeitsangaben. Fehlkonfiguration: fehlende Liegenschaft/Einheit oder ungültige Auftragsangaben, die das Anlegen blockieren."
                bind:value={
                    () => filters.status,
                    (value) => {
                        if (value === "all" || value === "review" || value === "misconfigured")
                            onFiltersChange({ ...filters, status: value })
                    }
                }
            />
            <Input
                type="number"
                placeholder="Nr größer als"
                min={0}
                step={1}
                bind:value={
                    () => filters.numberGreaterThan,
                    (value) => onFiltersChange({ ...filters, numberGreaterThan: String(value ?? "") })
                }
            />
        </div>
    </div>
    <Table
        rowActions={{
            resolve: (row) =>
                buildTableRowActionSet(
                    ((row) => {
                        const actions: import("@kontextwerk/web-sdk/core/layout/table").TableRowAction[] = []

                        actions.push(
                            ...AddressRowActions({
                                row: row as LoadListTableRow,
                                disabled: locked || readOnly || (row as LoadListTableRow).removed,
                                onEditUnits,
                                onCopyRow,
                                onShowDetails,
                                readOnly,
                            })
                        )

                        return actions
                    })(row) ?? [],
                    { primaryId: null, inlineIds: ["copy-unit-row"] }
                ),
        }}
        {columns}
        {getRows}
        {defaultSort}
        {pageSize}
        {refreshKey}
        onRowsLoaded={(result) => onRowsLoaded?.(result.rows as LoadListTableRow[])}
        {onSelectionChange}
        theme="dark"
        mode="table-scroll"
        showFilters={false}
        showActions={true}
        actionsHeaderLabel="Aktionen"
        actionsWidth={128}
        setRefreshFunction={(fn) => {
            onSetRefreshFunction(fn)
        }}
        setRowsUpdater={(updater) => {
            onSetRowsUpdater(updater)
        }}
        enableSelection={!locked && !readOnly}
        rowControls={!readOnly ? rowControlsFor : undefined}
        getRowState={rowStateFor}
    >
        {#snippet batchActions(context: TableSelectionContext)}
            <CTA
                type="secondaryButton"
                content="Entfernen"
                icon={mdiMinus}
                small={true}
                tooltipContent="Nimmt die ausgewählten aktiven Zeilen aus der Sammelplanung. Die Liegenschaften werden nicht gelöscht und können mit „Hinzufügen“ wieder aktiviert werden."
                disabled={locked || readOnly || !context.selectedRows.some((row) => !row.removed)}
                onClick={() => applyBulkAction(context, true)}
            />
            <CTA
                type="secondaryButton"
                content="Hinzufügen"
                icon={mdiPlus}
                small={true}
                tooltipContent="Aktiviert die ausgewählten, zuvor entfernten Zeilen wieder für die Sammelplanung. Die bisherige Einheit wird wieder gewählt, sofern sie noch frei ist."
                disabled={locked || readOnly || !context.selectedRows.some((row) => row.removed)}
                onClick={() => applyBulkAction(context, false)}
            />
        {/snippet}
        {#snippet empty()}
            <div class="empty-row">
                {filters.search || filters.status !== "all" || filters.numberGreaterThan
                    ? "Keine Liegenschaften für diese Filter gefunden."
                    : "Keine Liegenschaften vorhanden."}
            </div>
        {/snippet}
        {#snippet tableCell({ column, row })}
            {@render renderCell(column, row)}
        {/snippet}
        {#snippet cardField({ column, row })}
            {@render renderCell(column, row)}
        {/snippet}
    </Table>
</div>

<style lang="less" global>
    .batch-address-table {
        container-type: inline-size;
        width: 100%;
        :global(.batch-row-removed) {
            opacity: 0.6;
        }

        :global(td) {
            vertical-align: bottom;
        }

        :global(.cell-content) {
            display: flex;
            align-items: flex-end;
            min-height: 60px;
        }

        .batch-address-filters {
            margin-bottom: 1.2rem;
        }
    }
</style>
